'use client';

import { useEffect } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import useOpenRewardModal from '../../hooks/useOpenRewardModal';
import { usePdpModalConfig } from '../../hooks/usePdpModalConfig';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { shouldUsePdpModal } from '../../utils/helpers/urlAlias';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { AsideModal, useAsideModalClose } from '../AsideModal';
import Button from '../Button';
import Close from '../icons/Close';
import Spinner from '../icons/Spinner';
import { ProductDetailsProps } from './ProductDetails';
import { getSectionBuilder } from './SectionBuilders/getSectionBuilder';
import { resolveModalBenefitCategorySelector } from './utils/resolveModalBenefitCategorySelector';
import { getNavigationItems, sectionAnchorId } from './utils/sections';

type ProductDetailsModalModeProps = ProductDetailsProps & {
  keyFromComponent?: string;
  /**
   * Section to scroll to once the modal is open, e.g. `section-offers` — the
   * PDP-modal equivalent of the legacy aside panel's `id_scroll_to` (CMS-1924).
   */
  idScrollTo?: string;
};

/**
 * Title frame of the `#usePDP` modal: product name + close button in one row
 * (designer remark — the X sits inside the title frame, not floating over the
 * content). The title never truncates: long names wrap onto further lines
 * while the X keeps its size. Must render inside `AsideModal` so the close
 * hook picks up the slide-out transition.
 */
const PdpModalHeader: React.FC<{ title: string | undefined }> = ({ title }) => {
  const closeAsideModal = useAsideModalClose();
  return (
    <div className="flex items-center py-2 bg-fill/base px-spacing/padding/x">
      {/* Drawer product-name title as an <h2> so the injected section <h2>s have a
          heading naming the product (the drawer has no own <h1> — the host page
          owns it) instead of landing anonymously in the host outline (CMS-1869). */}
      <h2 className="py-spacing/padding/y-sm alpha-subheading-2 text-text/primary m-0 min-w-0 flex-1 break-words">
        {title}
      </h2>
      <Button
        endIcon={<Close className="size-6" />}
        styleType="neutral"
        className="flex size-11 shrink-0 items-center justify-center border-0 !p-0"
        variant="outline"
        aria-label="Close"
        onClick={closeAsideModal}
      />
    </div>
  );
};

/**
 * Aside-modal frame: product-name title, then the vertical's sticky nav and its sections. Anything
 * the modal needs to adjust in the props happens here, before the builder runs — the `#usePDP`
 * benefit filter (CMS-1924).
 */
export const ProductDetailsModalMode: React.FC<ProductDetailsModalModeProps> = (
  props,
) => {
  const { product, keyFromComponent = product?.uid ?? '', idScrollTo } = props;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const {
    closeModal,
    rewardModalComponentKey: productDetailModalComponentKey,
    rewardModalProductId: productDetailModalProductId,
  } = useOpenRewardModal();

  const isOpen =
    !!product &&
    productDetailModalProductId === product.uid &&
    keyFromComponent === productDetailModalComponentKey;

  // The #usePDP-marker modal mirrors the canonical PDP page's editor-configured
  // benefit filter (fetched only while open); no config → show every category (CMS-1924).
  const usePdp = shouldUsePdpModal(product);
  const { config, loading } = usePdpModalConfig({
    urlAlias: product?.url_alias,
    enabled: usePdp && isOpen,
    getPdpModalConfigAction: props.getPdpModalConfigAction,
    isPreview: props.isPreview,
  });

  // Scroll to the requested section once the content is actually rendered.
  // Mirrors the legacy aside panel's `id_scroll_to`, delay included.
  const canScrollTo = isOpen && !loading ? idScrollTo : undefined;
  useEffect(() => {
    if (!canScrollTo) {
      return;
    }
    const timeout = setTimeout(() => {
      document
        .getElementById(canScrollTo)
        ?.scrollIntoView({ behavior: 'smooth' });
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [canScrollTo]);

  if (!product) {
    return null;
  }

  // Designer-spec width (448px / 1080px) is scoped to the #usePDP path; the
  // pre-existing `_productDetail` modal keeps the shipped `small` geometry.
  const size = usePdp ? 'pdp' : 'small';

  if (loading) {
    return (
      <AsideModal
        size={size}
        isOpen={isOpen}
        onClose={() => closeModal()}
        hideCloseButton={usePdp}
      >
        {/* The header carries the only close control on the #usePDP path, so it
            must be present while the config is still loading too. */}
        {usePdp && <PdpModalHeader title={product.product_name} />}
        <div
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className="flex min-h-[90vh] w-full items-center justify-center"
        >
          <Spinner className="size-14 animate-spin md:size-20 lg:size-28" />
        </div>
      </AsideModal>
    );
  }

  const benefitCategorySelector = resolveModalBenefitCategorySelector({
    usePdp,
    pdpPageSelector: config?.benefitCategorySelector,
    editorConfiguredSelector: props.benefitCategorySelector,
    product,
  });
  // The footer's apply link follows the CTA around it (CMS-2043): the opening card's override
  // first — the user came from it, as the legacy panel did — then the PDP page hero's
  // authored CTA; without either, the product's own link.
  const ctaLinkOverride = props.ctaLinkOverride ?? config?.heroCtaLinkOverride;
  const effectiveProps = { ...props, benefitCategorySelector, ctaLinkOverride };
  const { Navigation, Footer, sections } = getSectionBuilder(props.vertical)(
    effectiveProps,
    staticText,
  );

  return (
    <AsideModal
      size={size}
      isOpen={isOpen}
      onClose={() => closeModal()}
      hideCloseButton={usePdp}
    >
      {/* Grows with the content (not a fixed screen height) so the sticky footer's range spans
          the whole scroll of the panel; a column, so a short panel still has the footer at its
          bottom edge. `dvh`, as iOS Safari's `vh` is taller than the panel while its toolbar
          shows. */}
      <div className="flex min-h-dvh flex-col">
        {/* Designer remark (CMS-1924): on the marker path the X lives inside the
            title frame; the pre-existing `_productDetail` modal keeps its header
            and AsideModal's floating close button untouched. */}
        {usePdp ? (
          <PdpModalHeader title={product.product_name} />
        ) : (
          <div className="py-2 bg-fill/base ">
            {/* Drawer product-name title as an <h2> so the injected section <h2>s have a
                heading naming the product (the drawer has no own <h1> — the host page
                owns it) instead of landing anonymously in the host outline (CMS-1869). */}
            <h2 className="py-spacing/padding/y-sm px-spacing/padding/x alpha-subheading-2 text-text/primary m-0">
              {product.product_name}
            </h2>
          </div>
        )}
        {/* Canonical PDP page hero (CMS-1924 scope change) — app-rendered via
            the slot, marker path only. Called even without a config so the app
            can fall back to a product-derived hero (Benefits-fallback spirit). */}
        {usePdp &&
          props.renderPdpModalHero?.({
            config,
            product,
            ctaLinkOverride: props.ctaLinkOverride,
          })}
        <div className="relative flex flex-col justify-between gap-6 px-padding/x py-spacing/padding/y-sm">
          <Navigation
            {...effectiveProps}
            navigationItems={getNavigationItems(sections)}
            SPACER_COUNT={4}
          />
          {sections.map((section) => (
            <div key={section.id} id={sectionAnchorId(section.id)}>
              {section.render()}
            </div>
          ))}
        </div>
        {/* Pinned to the panel's bottom edge (the panel is the scroll container), so it spans the
            panel only, never the scrim, and unmounts with the panel. In flow it is the last
            block, which keeps the last section readable above it at the end of the scroll;
            `mt-auto` holds it at the bottom when the content is shorter than the panel. */}
        {Footer && (
          <div
            data-component="product-details-panel-footer"
            className="sticky bottom-0 z-sticky mt-auto"
          >
            <Footer {...effectiveProps} />
          </div>
        )}
      </div>
    </AsideModal>
  );
};
